'use client'

import { IconPromo } from '@/app/components/atoms/Icons'
import { useAppPromotionCookie } from '@/app/lib/hooks/useAppPromotionCookie'
import { cx } from '@/app/lib/utils'
import { APP_TAG_LABEL, GENERIC_PROMO_LABEL } from './constants'
import styles from './PriceTag.module.scss'
import type { PriceTagProps } from './types'

const ICON_SIZE = { m: 24, l: 32 } as const

/**
 * Badge cuadrado de promoción/descuento — mismo diseño de referencia que
 * `eci-firefly-uikit` (`EventCard.tsx`/`.promoBadge`, ver Figma 4694:186451):
 * cuadrado `--color-promo` (rojo) con el texto del descuento o, en su
 * defecto, el icono genérico de promoción. Siempre rojo, sin variante de
 * color alternativa — toda indicación de descuento/información especial
 * debe ser consistente en toda la web. `null` (sin rebaja ni promoción) no
 * renderiza nada, igual que la card de Agenda del proyecto de referencia
 * (sin badge, solo "Desde X €" vía `Price`).
 *
 * Único punto de interactividad cliente de toda la feature de precios: lee la
 * cookie de la app post-montaje (`useAppPromotionCookie`) para decidir si
 * sustituye el contenido del badge por "App" (FR-014/FR-015). Todo lo demás
 * (descuento/icono/label) llega ya calculado por `resolveEventPrice` — este
 * átomo no vuelve a hacer cálculo de negocio.
 */
export function PriceTag({
  tag,
  hasAppPromotions = false,
  size = 'm',
  className,
}: PriceTagProps) {
  const isAppContext = useAppPromotionCookie()
  const badgeClassName = cx(styles.badge, size === 'l' && styles.badgeL, className)

  if (hasAppPromotions && isAppContext) {
    return (
      <span className={badgeClassName}>
        <span className={styles.text}>{APP_TAG_LABEL}</span>
      </span>
    )
  }

  if (!tag) return null

  if (tag.kind === 'discount') {
    return (
      <span className={badgeClassName}>
        <span className={styles.text}>{`-${tag.percentage}%`}</span>
      </span>
    )
  }

  if (tag.kind === 'label') {
    return (
      <span className={badgeClassName}>
        <span className={styles.text}>{tag.text}</span>
      </span>
    )
  }

  return (
    <span className={badgeClassName}>
      <IconPromo size={ICON_SIZE[size]} label={GENERIC_PROMO_LABEL} />
    </span>
  )
}
